// src/pages/LowVolRally.jsx
export default function LowVolRally() {
    return (
      <article className="prose prose-invert max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold tracking-tight mb-2">
          Stay Invested, Buy the Convexity: Reading a Low-Vol Rally
        </h1>
  
        <p>
          The market's come a long way off the April lows, and it's done it
          with almost no volatility along the way. That combination — big
          move, low vol — is worth sitting with, because it cuts both ways.
          On one hand it's a genuinely high-quality rally by that measure. On
          the other, there's a lot already priced in, which means real room
          for disappointment if any of the underlying assumptions crack.
        </p>
  
        <h2 className="text-2xl font-bold">The Bull Case, Plainly</h2>
        <ul>
          <li><strong>Rate cutting cycle:</strong> underway and expected to continue, with policy rates normalizing toward roughly 3% — already the market's base case.</li>
          <li><strong>Fiscal tailwinds:</strong> next year brings additional fiscal support that should help the market absorb the drag from tariffs.</li>
          <li><strong>AI investment cycle:</strong> not a Q4 2025 story — this has been running since early 2023. Ten quarters into a cycle isn't obviously a top, even if any single day can feel exuberant.</li>
        </ul>
        <p>US equities remain the preferred place to be, not because of momentum, but because the fundamentals underneath it are still intact.</p>
  
        <h2 className="text-2xl font-bold">The Crowding Question</h2>
        <p>
          Everyone thinks AI works. Everyone thinks the Fed cuts. Everyone
          wants to be long US equities. That's the setup people worry about —
          but consensus opinion and actual positioning aren't the same thing.
          Talk is cheap; capital allocation tends to run more cautious than
          the consensus narrative suggests. That gap between what people say
          and what they actually do is part of why the grind higher has
          continued rather than snapping.
        </p>
        <p>
          The expected path: a bit of a pause in Q4, resuming into 2026. Not
          a call with high conviction — just the base case, with the honest
          caveat that any single forecast is only as good as anyone else's guess.
        </p>
  
        <h2 className="text-2xl font-bold">What's Actually Worth Watching</h2>
        <ul>
          <li><strong>Diversification away from fiat:</strong> the move into gold and bitcoin isn't a 2025 phenomenon — it's been building since the post-pandemic fiscal expansion. Mag 7 exposure functions similarly, as a partial hedge against currency debasement. Worth monitoring, not necessarily worth chasing.</li>
          <li><strong>The government shutdown:</strong> the market is treating it as a non-event, largely because the missing economic data means there's nothing concrete to react to. That's the irony — no data means the rosy read can't be disproven either way. A Fed that's data-dependent operating without data is a real risk if the shutdown drags on, even if it's currently suppressing volatility rather than raising it.</li>
        </ul>
  
        <h2 className="text-2xl font-bold">The Trade</h2>
        <p>
          Stay invested. Use the current low level of volatility to buy
          convexity while it's cheap — index puts on equities, calls on the
          dollar as a flight-to-quality hedge, and structures in rates to
          benefit if things trade outside the current range. The point isn't
          to fade the rally. It's to stay in it while protecting the downside
          cheaply, since low realized vol makes that protection unusually
          inexpensive relative to where it's traded historically.
        </p>
  
        <div className="my-6">
          {/* Space for relevant chart if you want one */}
        </div>
  
        <h2 className="text-2xl font-bold">Closing Thoughts</h2>
        <p>
          Nothing here says get out. It says stay in, stay aware of what's
          priced in, and use cheap optionality to protect against the
          exogenous shock that low-vol rallies always eventually have to
          answer for. Crowded consensus, thin data, a lot already priced —
          none of that is a reason to leave the trade. It's a reason to hedge it.
        </p>
        <p>That's the whole post. Stay long, buy the convexity.</p>
      </article>
    );
  }